import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useProducts } from '../contexts/ProductContext';
import { Product, categories } from '../utils/data';
import { getDefaultImageForCategory } from '../utils/categoryImages';
interface FormData {
  dietitianName: string;
  credentials: string;
  productName: string;
  productDescription: string;
  productCategory: string;
  productPrice: string;
  productLink: string;
  imageLink: string;
  productReview: string;
  productRating: string;
  termsAgreed: boolean;
}
const SubmitProduct: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    addProduct
  } = useProducts();
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm<FormData>();
  const onSubmit = (data: FormData) => {
    try {
      // Generate a unique ID for the new product
      const newProductId = `product-${Date.now()}`;
      // Use category-specific image if no image URL is provided
      const productImage = data.imageLink || getDefaultImageForCategory(data.productCategory);
      // Create the new product object
      const newProduct: Product = {
        id: newProductId,
        name: data.productName,
        description: data.productDescription,
        shortDescription: data.productDescription.substring(0, 100) + '...',
        image: productImage,
        price: data.productPrice,
        purchaseLink: data.productLink,
        category: data.productCategory,
        reviews: [{
          id: `review-${Date.now()}`,
          dietitianName: data.dietitianName,
          credentials: data.credentials,
          rating: parseInt(data.productRating),
          comment: data.productReview,
          date: new Date().toISOString().split('T')[0]
        }]
      };
      // Add the product to the global state
      addProduct(newProduct);
      // Show success message
      setIsSubmitted(true);
      reset();
    } catch (error) {
      setSubmitError('There was an error submitting your product. Please try again.');
    }
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-202">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-203">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-204">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-205">Submit a Product</h1>
          <p className="mt-2 text-lg text-gray-600" data-id="element-206">
            Are you a registered dietitian? Submit a product you recommend for
            inclusion in our marketplace.
          </p>
        </div>
        {isSubmitted ? <div className="bg-white rounded-lg shadow-md p-8 text-center" data-id="element-207">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-208">
              <CheckCircle className="h-6 w-6 text-green-600" data-id="element-209" />
            </div>
            <h2 className="mt-4 text-xl font-medium text-gray-900" data-id="element-210">
              Submission Received!
            </h2>
            <p className="mt-2 text-gray-600" data-id="element-211">
              Thank you for submitting a product to FayMarket. Our team will
              review your submission and contact you if we need additional
              information.
            </p>
            <button onClick={() => setIsSubmitted(false)} className="mt-6 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700" data-id="element-212">
              Submit Another Product
            </button>
          </div> : <div className="bg-white rounded-lg shadow-md p-6" data-id="element-213">
            {submitError && <div className="mb-6 p-4 bg-red-50 rounded-md flex items-start" data-id="element-214">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-215" />
                <p className="text-sm text-red-700" data-id="element-216">{submitError}</p>
              </div>}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-217">
              {/* Dietitian Information */}
              <div data-id="element-218">
                <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-219">
                  Dietitian Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-220">
                  <div data-id="element-221">
                    <label htmlFor="dietitianName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-222">
                      Full Name *
                    </label>
                    <input type="text" id="dietitianName" className={`block w-full border ${errors.dietitianName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('dietitianName', {
                  required: 'Name is required'
                })} data-id="element-223" />
                    {errors.dietitianName && <p className="mt-1 text-sm text-red-600" data-id="element-224">
                        {errors.dietitianName.message}
                      </p>}
                  </div>
                  <div data-id="element-225">
                    <label htmlFor="credentials" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-226">
                      Credentials *
                    </label>
                    <input type="text" id="credentials" placeholder="RD, RDN, etc." className={`block w-full border ${errors.credentials ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('credentials', {
                  required: 'Credentials are required'
                })} data-id="element-227" />
                    {errors.credentials && <p className="mt-1 text-sm text-red-600" data-id="element-228">
                        {errors.credentials.message}
                      </p>}
                  </div>
                </div>
              </div>
              {/* Product Information */}
              <div data-id="element-229">
                <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-230">
                  Product Information
                </h2>
                <div className="space-y-4" data-id="element-231">
                  <div data-id="element-232">
                    <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-233">
                      Product Name *
                    </label>
                    <input type="text" id="productName" className={`block w-full border ${errors.productName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productName', {
                  required: 'Product name is required'
                })} data-id="element-234" />
                    {errors.productName && <p className="mt-1 text-sm text-red-600" data-id="element-235">
                        {errors.productName.message}
                      </p>}
                  </div>
                  <div data-id="element-236">
                    <label htmlFor="productCategory" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-237">
                      Product Category *
                    </label>
                    <select id="productCategory" className={`block w-full border ${errors.productCategory ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productCategory', {
                  required: 'Category is required'
                })} data-id="element-238">
                      <option value="" data-id="element-239">Select a category</option>
                      <option value="Supplements" data-id="element-240">Supplements</option>
                      <option value="Meal Services" data-id="element-241">Meal Services</option>
                      <option value="Kitchen Tools" data-id="element-242">Kitchen Tools</option>
                      <option value="Nutrition Apps" data-id="element-243">Nutrition Apps</option>
                      <option value="Fitness Equipment" data-id="element-244">
                        Fitness Equipment
                      </option>
                      <option value="Other" data-id="element-245">Other</option>
                    </select>
                    {errors.productCategory && <p className="mt-1 text-sm text-red-600" data-id="element-246">
                        {errors.productCategory.message}
                      </p>}
                  </div>
                  <div data-id="element-247">
                    <label htmlFor="productDescription" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-248">
                      Product Description *{' '}
                      <span className="text-gray-500 font-normal" data-id="element-249">
                        (min. 25 characters)
                      </span>
                    </label>
                    <textarea id="productDescription" rows={3} className={`block w-full border ${errors.productDescription ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productDescription', {
                  required: 'Description is required',
                  minLength: {
                    value: 25,
                    message: 'Description must be at least 25 characters'
                  }
                })} data-id="element-250"></textarea>
                    {errors.productDescription && <p className="mt-1 text-sm text-red-600" data-id="element-251">
                        {errors.productDescription.message}
                      </p>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-252">
                    <div data-id="element-253">
                      <label htmlFor="productPrice" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-254">
                        Approximate Price *
                      </label>
                      <input type="text" id="productPrice" placeholder="$XX.XX or price range" className={`block w-full border ${errors.productPrice ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productPrice', {
                    required: 'Price is required'
                  })} data-id="element-255" />
                      {errors.productPrice && <p className="mt-1 text-sm text-red-600" data-id="element-256">
                          {errors.productPrice.message}
                        </p>}
                    </div>
                    <div data-id="element-257">
                      <label htmlFor="productLink" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-258">
                        Where to Purchase (URL) *
                      </label>
                      <input type="url" id="productLink" placeholder="https://" className={`block w-full border ${errors.productLink ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productLink', {
                    required: 'Purchase link is required',
                    pattern: {
                      value: /^https?:\/\/.+/i,
                      message: 'URL must start with http:// or https://'
                    }
                  })} data-id="element-259" />
                      {errors.productLink && <p className="mt-1 text-sm text-red-600" data-id="element-260">
                          {errors.productLink.message}
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-261">
                    <label htmlFor="imageLink" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-262">
                      Product Image URL
                    </label>
                    <input type="url" id="imageLink" placeholder="https:// (optional)" className={`block w-full border ${errors.imageLink ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('imageLink', {
                  pattern: {
                    value: /^https?:\/\/.+/i,
                    message: 'URL must start with http:// or https://'
                  }
                })} data-id="element-263" />
                    {errors.imageLink && <p className="mt-1 text-sm text-red-600" data-id="element-264">
                        {errors.imageLink.message}
                      </p>}
                  </div>
                </div>
              </div>
              {/* Review Information */}
              <div data-id="element-265">
                <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-266">
                  Your Professional Review
                </h2>
                <div className="space-y-4" data-id="element-267">
                  <div data-id="element-268">
                    <label htmlFor="productReview" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-269">
                      Professional Assessment *{' '}
                      <span className="text-gray-500 font-normal" data-id="element-270">
                        (min. 50 characters)
                      </span>
                    </label>
                    <textarea id="productReview" rows={4} placeholder="Please provide your professional opinion on this product and why you recommend it" className={`block w-full border ${errors.productReview ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productReview', {
                  required: 'Review is required',
                  minLength: {
                    value: 50,
                    message: 'Review must be at least 50 characters'
                  }
                })} data-id="element-271"></textarea>
                    {errors.productReview && <p className="mt-1 text-sm text-red-600" data-id="element-272">
                        {errors.productReview.message}
                      </p>}
                  </div>
                  <div data-id="element-273">
                    <label htmlFor="productRating" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-274">
                      Rating (1-5 stars) *
                    </label>
                    <select id="productRating" className={`block w-full border ${errors.productRating ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm`} {...register('productRating', {
                  required: 'Rating is required'
                })} data-id="element-275">
                      <option value="" data-id="element-276">Select a rating</option>
                      <option value="5" data-id="element-277">5 Stars - Excellent</option>
                      <option value="4" data-id="element-278">4 Stars - Very Good</option>
                      <option value="3" data-id="element-279">3 Stars - Good</option>
                      <option value="2" data-id="element-280">2 Stars - Fair</option>
                      <option value="1" data-id="element-281">1 Star - Poor</option>
                    </select>
                    {errors.productRating && <p className="mt-1 text-sm text-red-600" data-id="element-282">
                        {errors.productRating.message}
                      </p>}
                  </div>
                </div>
              </div>
              {/* Terms Agreement */}
              <div data-id="element-283">
                <div className="flex items-start" data-id="element-284">
                  <div className="flex items-center h-5" data-id="element-285">
                    <input id="termsAgreed" type="checkbox" className={`h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500 ${errors.termsAgreed ? 'border-red-300' : ''}`} {...register('termsAgreed', {
                  required: 'You must agree to the terms'
                })} data-id="element-286" />
                  </div>
                  <div className="ml-3" data-id="element-287">
                    <label htmlFor="termsAgreed" className="text-sm text-gray-700" data-id="element-288">
                      I certify that I am a registered dietitian or qualified
                      nutrition professional and that my review represents my
                      honest professional opinion.
                    </label>
                    {errors.termsAgreed && <p className="mt-1 text-sm text-red-600" data-id="element-289">
                        {errors.termsAgreed.message}
                      </p>}
                  </div>
                </div>
              </div>
              <div className="pt-4" data-id="element-290">
                <button type="submit" className="w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500" data-id="element-291">
                  Submit Product for Review
                </button>
              </div>
            </form>
          </div>}
      </div>
    </div>;
};
export default SubmitProduct;